// ShiftProvider — state shift kasir dipakai bersama Kasir (wajib shift
// terbuka untuk transaksi) & halaman Kas/Shift (US-REPORT-3, US-CASH-1).
// Disimpan per akun di localStorage agar tahan reload; server otoritatif
// saat online.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  closeShift as apiCloseShift,
  createCashMovement,
  getCurrentShift,
  openShift as apiOpenShift,
  type Shift,
  type ShiftClose,
} from "../api";
import { useAuth } from "./auth";
import { useBranch } from "./branch";

export type ShiftState = {
  id: string;
  status: "open" | "closed";
  openingCash: number;
  actualCash?: number;
};

// Cache shift dikunci per (akun, cabang aktif): satu laci terbuka PER CABANG
// (RFC-007 §3.2/§4). Tanpa branchId di kunci, shift cabang A bisa keliru
// dipakai saat berpindah ke cabang B.
function storageKey(accountId: string | undefined, branchId: string): string {
  return `pos.shift.${accountId ?? "guest"}.${branchId || "default"}`;
}

function loadShift(accountId: string | undefined, branchId: string): ShiftState | null {
  try {
    const raw = localStorage.getItem(storageKey(accountId, branchId));
    return raw ? (JSON.parse(raw) as ShiftState) : null;
  } catch {
    return null;
  }
}

function serverShiftId(value: Shift): string {
  return value.id ?? value.ID ?? "";
}

type ShiftValue = {
  shift: ShiftState | null;
  isOpen: boolean;
  /** False selama sesi/shift masih dimuat (hindari minta kas awal saat reload). */
  ready: boolean;
  /** Ringkasan rekonsiliasi shift terakhir yang ditutup (server). */
  lastClose: ShiftClose | null;
  open: (openingCash: number) => Promise<void>;
  close: (actualCash: number) => Promise<void>;
  cash: (type: "in" | "out", category: string, amount: number) => Promise<void>;
};

const ShiftContext = createContext<ShiftValue | null>(null);

export function ShiftProvider({ children }: { children: ReactNode }) {
  const { token, account, status } = useAuth();
  const { activeId: branchId } = useBranch();
  const accountId = account?.id;
  const [shift, setShift] = useState<ShiftState | null>(null);
  const [ready, setReady] = useState(false);
  const [lastClose, setLastClose] = useState<ShiftClose | null>(null);

  // Tunggu auth selesai dimuat dulu. Shift bersifat STORE-WIDE: ambil shift
  // terbuka dari server (lintas user) — bukan hanya cache per-akun. localStorage
  // dipakai sebagai fallback cepat/offline. Tanpa ini, kasir lain keliru
  // diminta kas awal padahal sudah dibuka admin/kasir lain.
  // Re-fetch saat token ATAU cabang aktif berubah: shift terbuka PER CABANG
  // (RFC-007). Ganti cabang -> token baru -> efek jalan ulang -> tarik shift
  // cabang baru. Server otoritatif dari branchId di token.
  useEffect(() => {
    if (status === "loading") return;
    let cancelled = false;
    setReady(false);
    setShift(loadShift(accountId, branchId)); // cache cabang ini (offline / cegah flicker)
    if (!token) {
      setReady(true);
      return;
    }
    getCurrentShift(token)
      .then((res) => {
        if (cancelled) return;
        if (res.shift) {
          setShift({
            id: serverShiftId(res.shift),
            status: "open",
            openingCash: res.shift.openingCash ?? 0,
            actualCash: res.shift.actualCash,
          });
        } else {
          setShift(null); // tak ada shift terbuka di cabang aktif
        }
      })
      .catch(() => undefined) // offline: pakai cache localStorage
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [status, accountId, token, branchId]);

  useEffect(() => {
    if (!ready) return;
    try {
      if (shift) localStorage.setItem(storageKey(accountId, branchId), JSON.stringify(shift));
      else localStorage.removeItem(storageKey(accountId, branchId));
    } catch {
      // abaikan
    }
  }, [shift, accountId, branchId, ready]);

  const open = useCallback(
    async (openingCash: number) => {
      setLastClose(null);
      if (token && accountId) {
        // Server idempoten store-wide: bila sudah ada shift terbuka, ia
        // mengembalikan shift itu (pakai openingCash-nya, bukan input ini).
        const opened = await apiOpenShift(token, accountId, openingCash);
        const id = serverShiftId(opened) || `local-${crypto.randomUUID()}`;
        setShift({ id, status: "open", openingCash: opened.openingCash ?? openingCash, actualCash: opened.actualCash });
      } else {
        setShift({ id: `local-${crypto.randomUUID()}`, status: "open", openingCash });
      }
    },
    [token, accountId],
  );

  const close = useCallback(
    async (actualCash: number) => {
      const current = shift;
      setShift(current ? { ...current, status: "closed", actualCash } : current);
      if (token && current?.id && !current.id.startsWith("local-")) {
        const summary = await apiCloseShift(token, current.id, actualCash);
        setLastClose(summary);
      }
    },
    [token, shift],
  );

  const cash = useCallback(
    async (type: "in" | "out", category: string, amount: number) => {
      if (!shift?.id) throw new Error("Shift belum dibuka");
      if (token && !shift.id.startsWith("local-")) {
        await createCashMovement(token, { shiftId: shift.id, type, category, amount, actorId: accountId });
      }
    },
    [token, shift, accountId],
  );

  const value = useMemo<ShiftValue>(
    () => ({ shift, isOpen: shift?.status === "open", ready, lastClose, open, close, cash }),
    [shift, ready, lastClose, open, close, cash],
  );

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

export function useShift(): ShiftValue {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error("useShift() harus dipakai di dalam <ShiftProvider>");
  return ctx;
}
